import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { StandardIds } from '../../../../shared-services/crm-services/field.service';
import { FieldValueInterface, FormInputComponent, ObjectType } from '../../../../tokens-and-interfaces';
import { FieldValidation } from '../../field-validation';
import { AutoCompleteCallback, AutocompleteService, DropdownItem, TimeZone } from '../autocomplete.service';
import { UpdaterService } from '../updater.service';
import { stopChromeAutofill } from '../stop-chrome-autofill';

const NO_VALUE_FOR_NAME = '<NO_VALUE_FOR_NAME>';

interface CompanyAddressInfo {
  address: string;
  city: string;
  zip: string;
  country: string;
  country_short: string;
  region: string;
  region_short: string;
  name: string;
  place_id: string;
}

@Component({
  templateUrl: './google-place-selector.component.html',
  providers: [AutocompleteService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    GoogleMapsModule,
  ],
  styleUrls: ['../renderers.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GooglePlaceSelectorComponent extends FormInputComponent implements AutoCompleteCallback, OnInit {
  NO_VALUE_FOR_NAME = NO_VALUE_FOR_NAME;

  override readOnly = false;
  private _control: FormControl<DropdownItem | string> = new FormControl();
  private readonly locations$$ = new ReplaySubject<DropdownItem[]>(1);
  protected locations$ = this.locations$$.asObservable();
  private readonly txtQueryChanged = new Subject<string>();

  override objectType: ObjectType;
  label = '';
  protected validation: FieldValidation;
  protected googleMap: google.maps.Map;

  set control(control: FormControl<string>) {
    this._control = control;
    this._control.markAsPristine();
  }
  get control(): UntypedFormControl {
    return this._control;
  }

  set formGroup(group: UntypedFormGroup) {
    // Do nothing
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  get value(): FieldValueInterface {
    const controlValue = this._control.value;
    let companyName = '';
    if (controlValue) {
      if (typeof controlValue != 'string') {
        companyName = (controlValue as { mainText: string })?.mainText?.toString() ?? '';
      } else {
        companyName = controlValue;
      }
    }

    return {
      fieldId: this._fieldId(),
      stringValue: companyName,
    };
  }

  disableAutoComplete: string = stopChromeAutofill();

  constructor(
    private autocompleteService: AutocompleteService,
    private updaterService: UpdaterService,
    readonly destroyRef: DestroyRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.txtQueryChanged
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(200), distinctUntilChanged())
      .subscribe((model) => {
        this.autocompleteService.getChoices(model, (choices: DropdownItem[]): void => {
          this.locations$$.next(choices);
        });
      });

    this._control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((searchTerm) => {
      if (typeof searchTerm != 'string') {
        this.txtQueryChanged.next(searchTerm.mainText);
      } else {
        this.txtQueryChanged.next(searchTerm);
      }
    });
  }

  /*  AutoComplete  */
  onMapReady(googleMap: google.maps.Map): void {
    this.googleMap = googleMap;
    this.autocompleteService.init(googleMap, this);
  }

  /* Callback function from Autocomplete Service when a business is selected. */
  setBusinessSelectedDetailsCallback(
    place_id: string,
    place: google.maps.places.PlaceResult,
    timeZone: TimeZone,
  ): void {
    const companyAddressDetails: CompanyAddressInfo = {} as CompanyAddressInfo;
    companyAddressDetails.place_id = place_id;
    companyAddressDetails.name = place?.name ?? '';

    const street_number = this.extractFromAddress(place?.address_components ?? [], 'street_number');
    const address = this.extractFromAddress(place?.address_components ?? [], 'route');
    // note: the following is the standard Canada/US address format, it may vary depending on country
    // ISO-19160-4:2023 could be a source for formatting, but googleMaps API doesn't deal with that yet
    const formattedAddress = (
      (street_number != null ? street_number : '') +
      ' ' +
      (address != null ? address : '')
    ).trim();
    companyAddressDetails.address = formattedAddress ?? '';

    companyAddressDetails.city = this.extractFromAddress(place?.address_components ?? [], 'locality');
    companyAddressDetails.zip = this.extractFromAddress(place?.address_components ?? [], 'postal_code');
    companyAddressDetails.country = this.extractFromAddress(place?.address_components ?? [], 'country');
    companyAddressDetails.country_short = this.extractFromAddress(
      place?.address_components ?? [],
      'country',
      'short_name',
    );

    companyAddressDetails.region = this.extractFromAddress(
      place?.address_components ?? [],
      'administrative_area_level_1',
    );
    companyAddressDetails.region_short = this.extractFromAddress(
      place?.address_components ?? [],
      'administrative_area_level_1',
      'short_name',
    );

    const clearIfEmpty = false;

    if (!this.isEditing) {
      this.updaterService.updateStringField(
        StandardIds.CompanyPrimaryAddressLine1,
        companyAddressDetails.address,
        clearIfEmpty,
      );
      this.updaterService.updateStringField(
        StandardIds.CompanyPrimaryAddressLine2,
        this.extractFromAddress(place?.address_components ?? [], 'floor'),
        clearIfEmpty,
      );
      this.updaterService.updateStringField(
        StandardIds.CompanyPrimaryAddressCity,
        companyAddressDetails.city,
        clearIfEmpty,
      );
      this.updaterService.updateStringField(
        StandardIds.CompanyPrimaryAddressState,
        companyAddressDetails.region,
        clearIfEmpty,
      );
      this.updaterService.updateStringField(
        StandardIds.CompanyPrimaryAddressPostalCode,
        companyAddressDetails.zip,
        clearIfEmpty,
      );
      this.updaterService.updateStringField(
        StandardIds.CompanyPrimaryAddressCountry,
        companyAddressDetails.country,
        clearIfEmpty,
      );
      this.updaterService.updateStringField(
        StandardIds.CompanyPrimaryAddressGooglePlaceID,
        companyAddressDetails.place_id,
        clearIfEmpty,
      );
      this.updaterService.updateStringField(StandardIds.CompanyWebsite, place?.website ?? '', clearIfEmpty);
      this.updaterService.updateFieldValue({
        fieldId: StandardIds.CompanyPhoneNumber,
        phoneFieldValues: {
          isoCountryCode: companyAddressDetails?.country_short ?? 'US',
          nationalNumber: place?.formatted_phone_number ?? '',
        },
      });
      this.updaterService.updateStringField(StandardIds.CompanyTimezone, timeZone?.timeZoneId ?? '', clearIfEmpty);

      const lat = place?.geometry?.location?.lat() || 0;
      const lng = place?.geometry?.location?.lng() || 0;

      this.updaterService.updateGeopointField(
        StandardIds.CompanyPrimaryAddressLatitudeLongitude,
        lat,
        lng,
        clearIfEmpty,
      );
    }
  }

  extractFromAddress(
    components: google.maps.GeocoderAddressComponent[],
    type: string,
    name_type = 'long_name',
  ): string {
    return (
      components
        .filter((component) => component.types.indexOf(type) === 0)
        .map((item) => item[name_type])
        .pop() || null
    );
  }

  companyOnSelected(location: DropdownItem | string): void {
    if (location) {
      if (typeof location != 'string') {
        this.autocompleteService.onSelect(location.placeId);
      } else {
        this.autocompleteService.onSelect(location);
      }
    }
  }

  companyDisplayValue(location: DropdownItem | string): string {
    if (location) {
      if (typeof location != 'string') {
        return location.mainText;
      }
      return location;
    }
    return '';
  }
}
